import React from 'react';
import { Box, Button, List, ListItemButton, ListItemIcon, ListItemText, Paper, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import SyncIcon from '@mui/icons-material/Sync';
import EditIcon from '@mui/icons-material/Edit';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useNavigate } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { feedSubtitle } from '../labels';
import { EmptyState, Loader, NeedsHousehold, PageHeader, useConfirm, useHousehold, useLoad, useNotify, useRun } from '../common';
import { useSheets } from '../tasks';

/** The calendars read through their iCal links: each person's and the household's shared one. */
const CalendarFeedsPage: React.FC = () => {
  const navigate = useNavigate();
  const { members } = useHousehold();
  const notify = useNotify();
  const run = useRun();
  const sheets = useSheets();
  const [confirm, confirmDialog] = useConfirm();
  const { data: feeds, setData: setFeeds, error, loading, reload } = useLoad(() => api.getCalendarFeeds());

  const replace = (feed: T.CalendarFeed) => setFeeds(current => current?.map(f => (f.publicId === feed.publicId ? feed : f)));

  /** Read it now, change it, or stop reading it. */
  const open = async (feed: T.CalendarFeed) => {
    const choice = await sheets.choose(feed.name, [
      { value: 'sync', text: 'Atualizar agora', detail: feed.isPaused ? 'Parado depois de várias falhas' : undefined, icon: <SyncIcon />, primary: true },
      { value: 'edit', text: 'Editar', detail: feed.canManage ? 'Nome, de quem é, link' : 'Só o nome', icon: <EditIcon /> },
      // Only whoever linked it, or the person it belongs to, unlinks it
      ...(feed.canManage ? [{ value: 'delete', text: 'Desligar', detail: 'Os eventos dele saem da app', icon: <LinkOffIcon /> }] : []),
    ], feedSubtitle(feed, members));
    if (choice === 'sync') {
      const synced = await run(() => api.syncCalendarFeed(feed.publicId));
      if (!synced) return;
      replace(synced);
      notify(synced.lastError ?? `${synced.name}: ${synced.eventCount} eventos`, { severity: synced.lastError ? 'warning' : undefined });
    } else if (choice === 'edit') {
      navigate(`/casa/eventos/calendarios/${feed.publicId}`);
    } else if (choice === 'delete') {
      // No "Desfazer": the link is not kept, so it would have to be pasted again
      if (!await confirm({
        title: 'Desligar', text: `Deixar de ler "${feed.name}"? Os eventos dele saem da app; para voltar, cola o link outra vez.`,
        confirmText: 'Desligar', danger: true,
      })) return;
      if (await run(() => api.deleteCalendarFeed(feed.publicId)) === undefined) return;
      setFeeds(current => current?.filter(f => f.publicId !== feed.publicId));
      notify(`${feed.name} desligado`);
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <PageHeader
        title="Calendários"
        subtitle="Os calendários do Google, Outlook ou iCloud de cada pessoa, e o partilhado da casa. A app só os lê, de 30 em 30 minutos; os eventos aparecem na Semana."
        actions={<Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/casa/eventos/calendarios/novo')}>Ligar</Button>}
      />
      <Loader loading={loading} error={error} onRetry={reload} hasData={feeds !== undefined}>
        {feeds && feeds.length === 0 && (
          <EmptyState title="Ainda não há calendários ligados" text="Usa «Ligar» e cola o endereço secreto em formato iCal de um calendário." />
        )}
        {feeds && feeds.length > 0 && (
          <Paper variant="outlined">
            <List disablePadding>
              {feeds.map(f => (
                <ListItemButton key={f.publicId} divider onClick={() => open(f)} sx={{ minHeight: 56 }}>
                  <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}><EventIcon /></ListItemIcon>
                  <ListItemText
                    primary={f.name}
                    secondary={<>
                      {feedSubtitle(f, members)}
                      {f.lastError && <Typography component="span" variant="caption" color="error" sx={{ display: 'block' }}>{f.lastError}</Typography>}
                    </>}
                    slotProps={{ primary: { fontWeight: 600 } }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}
      </Loader>
      {sheets.element}
      {confirmDialog}
    </Box>
  );
};

const CalendarFeedsRoute: React.FC = () => <NeedsHousehold><CalendarFeedsPage /></NeedsHousehold>;
export default CalendarFeedsRoute;
